import type { Station } from '../api/types';
import { useCountdown, useDeadline, useElapsed } from '../hooks/useCountdown';
import { formatCountdown, STATION_TYPE_LABEL } from '../lib/format';
import { Badge } from './ui';

// The one word staff scan the list for. Ordered by what matters most: a seat
// about to run out beats everything; a fault beats a normal state.
export function stationState(station: Station): {
  label: string;
  tone: 'slate' | 'green' | 'amber' | 'red' | 'sky';
} {
  const session = station.active_session;
  // A PS5 still being played after its time ran out beats everything: the
  // console cannot stop itself.
  if (station.time_up && !session) return { label: "Time's up", tone: 'red' };
  if (station.status === 'maintenance') return { label: 'Maintenance', tone: 'slate' };
  if (session?.warning_active && !session.paused) return { label: 'Under 5 min', tone: 'red' };
  if (session?.paused) return { label: 'Paused', tone: 'amber' };
  if (session) return { label: 'In use', tone: 'green' };
  if (station.shutdown_requested_at) return { label: 'Shutting down', tone: 'amber' };
  if (isUnblocked(station)) return { label: 'Unblocked', tone: 'sky' };
  return { label: 'Free', tone: 'slate' };
}

// A free seat whose screen is open: nobody is seated, yet the agent has been
// told not to lock. Timed (unlocked_until set) or open-ended (not set). A PS5
// counts once its TV is paired — the TV is what blanks its screen.
export function isUnblocked(station: Station): boolean {
  return (
    !station.active_session &&
    station.agent.paired &&
    !station.agent.locked &&
    station.status !== 'maintenance'
  );
}

// A PC going quiet right after staff shut it down is the expected outcome,
// not a fault — the "Shutting down" state says so instead. A PS5 with its TV
// paired is held to the same rule: a quiet TV cannot blank the screen.
export function agentFault(station: Station): boolean {
  return (
    station.agent.paired &&
    !station.agent.online &&
    !station.shutdown_requested_at
  );
}

// The small clock on a row: time left for a session, time until relock for an
// unblocked seat, nothing otherwise.
// A PS5 past its booked time: counts up from when it ran out until staff mark
// it free.
function OverTimeClock({ endedAt }: { endedAt: string }) {
  const over = useElapsed(endedAt);
  return (
    <span className="font-mono text-sm font-semibold tabular-nums text-rose-300">
      +{formatCountdown(over)}
    </span>
  );
}

function RowClock({ station }: { station: Station }) {
  const session = station.active_session;
  const remaining = useCountdown(
    session ? (session.seconds_remaining ?? (session.minutes_remaining ?? 0) * 60) : null,
    session?.session_id ?? '',
    Boolean(session && !session.paused),
  );
  const untilRelock = useDeadline(session ? null : station.unlocked_until);

  if (session) {
    return (
      <span
        className={`font-mono text-sm tabular-nums ${
          session.paused
            ? 'text-amber-300'
            : session.warning_active
              ? 'text-rose-300'
              : 'text-emerald-300'
        }`}
      >
        {formatCountdown(remaining)}
      </span>
    );
  }
  if (station.time_up) {
    return <OverTimeClock endedAt={station.time_up.ended_at} />;
  }
  if (untilRelock !== null) {
    return (
      <span className="font-mono text-sm tabular-nums text-sky-300">
        {formatCountdown(untilRelock)}
      </span>
    );
  }
  return null;
}

export function StationList({
  stations,
  selectedId,
  onSelect,
}: {
  stations: Station[];
  selectedId: string | null;
  onSelect: (station: Station) => void;
}) {
  return (
    <ul className="divide-y divide-slate-800 overflow-hidden rounded-xl border border-slate-800">
      {stations.map((station) => {
        const state = stationState(station);
        const selected = station.station_id === selectedId;
        const session = station.active_session;
        return (
          <li key={station.station_id}>
            <button
              type="button"
              onClick={() => onSelect(station)}
              aria-current={selected ? 'true' : undefined}
              className={`flex min-h-16 w-full items-center gap-3 px-3 py-2.5 text-left transition-colors ${
                selected
                  ? 'bg-sky-500/15 ring-1 ring-inset ring-sky-500/60'
                  : station.time_up && !session
                    ? 'animate-pulse bg-rose-500/20 hover:bg-rose-500/30'
                    : 'hover:bg-slate-900'
              }`}
            >
              {/* The agent dot: green online, amber paired-but-quiet, grey no
                  agent. Faults must be visible from the list, not just the
                  detail — a quiet PC is why "start" seemed to do nothing. A
                  PS5's agent is the app on its TV. */}
              <span
                className={`h-2.5 w-2.5 shrink-0 rounded-full ${
                  !station.agent.paired
                    ? 'bg-slate-600'
                    : station.agent.online
                      ? 'bg-emerald-400'
                      : 'bg-amber-400'
                }`}
                title={
                  station.station_type === 'ps5'
                    ? !station.agent.paired
                      ? 'No TV paired · timer only'
                      : station.agent.online
                        ? 'TV online'
                        : 'TV offline'
                    : !station.agent.paired
                      ? 'Not paired'
                      : station.agent.online
                        ? 'Agent online'
                        : 'Agent offline'
                }
              />
              <div className="min-w-0 flex-1">
                <p className="truncate font-semibold text-slate-100">
                  {station.name}
                  <span className="ml-2 text-xs font-normal text-slate-500">
                    {STATION_TYPE_LABEL[station.station_type] ?? station.station_type}
                  </span>
                </p>
                <p className="truncate text-xs text-slate-500">
                  {session
                    ? session.customer_name
                    : station.time_up
                      ? `${station.time_up.customer_name} — stop the game`
                      : state.label === 'Free'
                        ? 'Nobody seated'
                        : ''}
                </p>
              </div>
              <div className="flex flex-col items-end gap-1">
                <Badge tone={state.tone}>{state.label}</Badge>
                <RowClock station={station} />
              </div>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
